// Made-up overseas agents and the rates they gave, for judging the design (see lib/sample.ts).
// Real agents and every price they give are recorded in the Serb Mind vault, in that agent's note.
import { sampleOnly } from '../../lib/sample';
import type { Tone } from '../../components/tone';

import type { Agent, Rfq } from '@serb/shared';

export type { Agent, Rfq };

const A = (a: Omit<Agent, 'industry'>): Agent => ({ industry: 'Freight Forwarding', ...a });

export const AGENTS: Agent[] = sampleOnly([
  A({
    id: 'global-freight',
    name: 'Global Freight Ltd.',
    country: 'UAE',
    city: 'Dubai',
    address: 'JLT, Dubai, UAE',
    website: 'www.globalfreight.example',
    contact: 'Ahmed Al Mansoori',
    email: 'ahmed@globalfreight.example',
    phone: '+971 4 123 4567',
    services: ['FCL', 'LCL', 'Air'],
    coverage: 'Middle East, Europe, Asia',
    notes: 'Reliable agent for Europe and Asia routes. Good rates for FCL. Fast response.',
    status: 'Active',
    addedDaysAgo: 800,
  }),
  A({
    id: 'eurotrans',
    name: 'EuroTrans',
    country: 'Germany',
    city: 'Hamburg',
    address: 'Hafenstrasse 12, Hamburg, Germany',
    website: 'www.eurotrans.example',
    contact: 'Anna Schmidt',
    email: 'anna@eurotrans.example',
    phone: '+49 40 555 0101',
    services: ['FCL', 'LCL', 'Air'],
    coverage: 'Europe',
    notes: 'Strong on FCL from Hamburg and Rotterdam.',
    status: 'Active',
    addedDaysAgo: 650,
  }),
  A({
    id: 'mersin-forwarding',
    name: 'Mersin Forwarding',
    country: 'Turkey',
    city: 'Mersin',
    address: 'Free Zone, Mersin, Turkey',
    website: 'www.mersinfwd.example',
    contact: 'Petr Novak',
    email: 'petr@mersinfwd.example',
    phone: '+90 324 555 0188',
    services: ['Road', 'FCL'],
    coverage: 'Turkey, Black Sea',
    notes: 'Fast for trucking from Turkey.',
    status: 'Active',
    addedDaysAgo: 400,
  }),
  A({
    id: 'sky-logistics',
    name: 'Sky Logistics',
    country: 'Turkey',
    city: 'Istanbul',
    address: 'Tuzla, Istanbul, Turkey',
    website: 'www.skylogistics.example',
    contact: 'Elif Yilmaz',
    email: 'elif@skylogistics.example',
    phone: '+90 216 555 0123',
    services: ['FCL', 'LCL'],
    coverage: 'Turkey, Mediterranean',
    notes: '',
    status: 'Active',
    addedDaysAgo: 520,
  }),
  A({
    id: 'med-freight',
    name: 'Med Freight',
    country: 'Italy',
    city: 'Genoa',
    address: 'Via del Porto 8, Genoa, Italy',
    website: 'www.medfreight.example',
    contact: 'Marco Bianchi',
    email: 'marco@medfreight.example',
    phone: '+39 010 555 0177',
    services: ['FCL', 'LCL'],
    coverage: 'Mediterranean',
    notes: 'Slow to reply in August.',
    status: 'Active',
    addedDaysAgo: 300,
  }),
  A({
    id: 'aqua-shipping',
    name: 'Aqua Shipping',
    country: 'China',
    city: 'Shanghai',
    address: 'Pudong, Shanghai, China',
    website: 'www.aquashipping.example',
    contact: 'Li Wei',
    email: 'liwei@aquashipping.example',
    phone: '+86 21 5555 0166',
    services: ['FCL', 'LCL'],
    coverage: 'Far East',
    notes: '',
    status: 'Active',
    addedDaysAgo: 220,
  }),
  A({
    id: 'valencia-logistics',
    name: 'Valencia Logistics',
    country: 'Spain',
    city: 'Valencia',
    address: 'Puerto de Valencia, Spain',
    website: 'www.valencialog.example',
    contact: 'Carlos Ruiz',
    email: 'carlos@valencialog.example',
    phone: '+34 96 555 0144',
    services: ['FCL'],
    coverage: 'Spain, Portugal',
    notes: 'DG approvals can be slow.',
    status: 'Active',
    addedDaysAgo: 100,
  }),
  A({
    id: 'mu-logistics',
    name: 'MU Logistics',
    country: 'China',
    city: 'Guangzhou',
    address: 'Baiyun Airport Zone, Guangzhou, China',
    website: 'www.mulogistics.example',
    contact: 'Chen Hao',
    email: 'chen@mulogistics.example',
    phone: '+86 20 5555 0155',
    services: ['Air'],
    coverage: 'China air freight',
    notes: '',
    status: 'Active',
    addedDaysAgo: 25,
  }),
  A({
    id: 'ek-logistics',
    name: 'EK Logistics',
    country: 'UAE',
    city: 'Dubai',
    address: 'Dubai Airport Free Zone, UAE',
    website: 'www.eklogistics.example',
    contact: 'Rashid Khan',
    email: 'rashid@eklogistics.example',
    phone: '+971 4 555 0122',
    services: ['Air'],
    coverage: 'Worldwide air via Dubai',
    notes: '',
    status: 'Inactive',
    addedDaysAgo: 60,
  }),
]);

export const agentById = (id: string) => AGENTS.find((a) => a.id === id);

export type Outcome = Rfq['outcome'];
export const OUTCOME_TONE: Record<Outcome, Tone> = {
  Selected: 'success',
  'Not Selected': 'error',
  Pending: 'warning',
  'No Quote': 'neutral',
};

const R = (
  agentId: string,
  srb: string,
  date: string,
  rate: number | null,
  currency: Rfq['currency'],
  transit: string,
  outcome: Outcome,
  delivered?: Rfq['delivered'],
): Rfq => ({ agentId, srb, date, rate, currency, transit, outcome, delivered });

export const RFQS: Rfq[] = sampleOnly([
  R('global-freight', 'SRB-711', '2025-07-22', 6.4, 'USD', '5 days', 'Not Selected'),
  R('global-freight', 'SRB-640', '2025-07-20', 5.6, 'USD', '4 days', 'Selected'),
  R('global-freight', 'SRB-539', '2025-07-18', 120, 'USD', '23 days', 'Selected'),
  R('global-freight', 'SRB-596', '2025-07-15', 7400, 'GBP', '21 days', 'Not Selected'),
  R('global-freight', 'SRB-648', '2025-07-14', null, 'USD', '', 'No Quote'),
  R('global-freight', 'SRB-512', '2025-07-01', 5.2, 'USD', '3 days', 'Selected', 'On time'),
  R('eurotrans', 'SRB-596', '2025-07-16', 6900, 'USD', '21 days', 'Selected'),
  R('eurotrans', 'SRB-518', '2025-07-17', 3050, 'USD', '18 days', 'Selected'),
  R('eurotrans', 'SRB-701', '2025-07-02', 2200, 'EUR', '20 days', 'Selected', 'On time'),
  R('mersin-forwarding', 'SRB-625', '2025-07-19', 95, 'USD', '3 days', 'Selected'),
  R('sky-logistics', 'SRB-625', '2025-07-18', 110, 'USD', '4 days', 'Not Selected'),
  R('sky-logistics', 'SRB-648', '2025-07-21', null, 'USD', '', 'No Quote'),
  R('med-freight', 'SRB-539', '2025-07-18', 135, 'USD', '25 days', 'Not Selected'),
  R('aqua-shipping', 'SRB-703', '2025-07-20', 4900, 'USD', '14 days', 'Selected'),
  R('valencia-logistics', 'SRB-518', '2025-07-22', 2980, 'USD', '18 days', 'Pending'),
  R('mu-logistics', 'SRB-711', '2025-07-23', 5.7, 'USD', '7 days', 'Selected'),
  R('ek-logistics', 'SRB-711', '2025-07-23', 6.23, 'USD', '4–5 days', 'Not Selected'),
]);
